import React from "react";
import { createPortal } from "react-dom";

/**
 * DynamicRouteLoader — off-#root loading / transient-error indicator for
 * SEO-critical dynamic routes (BlogArticle, GuideArticle, VerticalHub).
 *
 * WHY THIS EXISTS
 * ───────────────
 * Emergent's `crawler-renderer` considers a page ready once meaningful
 * content is mounted under `#root` and DOES NOT wait for
 * `window.prerenderReady`. If a dynamic route mounts a placeholder like
 *
 *     <main data-testid="article-loading">Loading…</main>
 *
 * as the article slot BEFORE the API returns, that placeholder can be
 * captured as a valid long-lived crawler snapshot — with no <title>,
 * no <meta description>, no canonical, no H1. This is exactly what
 * corrupted 214+ URLs in the last production audit.
 *
 * WHAT THIS COMPONENT DOES
 * ────────────────────────
 * 1. Renders NOTHING into `#root` — the JSX returned to the route is a
 *    React portal that mounts into `document.body`, a sibling of `#root`.
 * 2. Provides a subtle, non-intrusive indicator for real human users
 *    (fixed top strip). No visible "page-like" markup, no <main>, no H1,
 *    no article shell, so a crawler that snapshots only `#root` cannot
 *    mistake it for the real page.
 * 3. For the `unavailable` variant, it also carries the existing
 *    `data-testid$="-unavailable"` marker so the smart-poll in
 *    `public/index.html` continues to refuse to flip `prerenderReady`
 *    while a transient error is on screen.
 * 4. The document `<title>` is intentionally NOT set by this component.
 *    A tab briefly falling back to the default browser title is
 *    acceptable UX; more importantly, it means a crawler that samples
 *    `document.title` during the loading window gets an empty/very
 *    short string and therefore does not treat the DOM as “ready”.
 *
 * Rule (see /app/docs/SEO_CONTENT_BIBLE.md §4.5):
 *   SEO-critical dynamic routes must not expose a loading shell as the
 *   initial `#root` content before SEO metadata/content is ready.
 */
export default function DynamicRouteLoader({ variant, testId }) {
  if (typeof document === "undefined") return null;
  const isUnavailable = variant === "unavailable";

  const styleWrap = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 60,
    display: "flex",
    justifyContent: "center",
    pointerEvents: "none",
  };
  const styleInner = {
    marginTop: "72px",
    padding: "6px 14px",
    borderRadius: "999px",
    background: "rgba(10,10,10,0.85)",
    color: "#fff",
    fontSize: "12px",
    letterSpacing: "0.02em",
    fontFamily: "Inter, system-ui, -apple-system, Segoe UI, sans-serif",
    boxShadow: "0 2px 12px rgba(0,0,0,0.18)",
    pointerEvents: "auto",
  };

  const label = isUnavailable
    ? "Content temporarily unavailable — please refresh in a moment"
    : "Loading…";

  return createPortal(
    <div
      style={styleWrap}
      data-testid={testId}
      data-off-root-indicator={isUnavailable ? "unavailable" : "loading"}
      aria-live={isUnavailable ? "assertive" : "polite"}
      role="status"
    >
      <span style={styleInner}>{label}</span>
    </div>,
    document.body,
  );
}
